import React, { useCallback, useEffect, useState } from 'react'
import { Card, Col, Form, Modal, Row, Spinner } from 'react-bootstrap'
import { Table, Tbody, Td, Th, Thead, Tr } from 'react-super-responsive-table'
import 'react-super-responsive-table/dist/SuperResponsiveTableStyle.css'
import 'bootstrap/dist/css/bootstrap.min.css'
import { LuGraduationCap } from 'react-icons/lu'
import { BsArrowClockwise, BsPencilFill, BsPlusLg } from 'react-icons/bs'
import toast from 'react-hot-toast'
import { useAuth } from './AuthContext'
import { useSidebar } from './SidebarContext'
import ProfileDropdown from './commonComponents/ProfileDropdown'
import { useInstitute } from './commonComponents/InstituteContext'
import ApiEndpoints from './commonComponents/ApiEndpoints'
import ExpandableReasonText from './commonComponents/ExpandableReasonText'

const STATUS_CONFIG = {
  pending: { label: 'Pending', bg: '#FFFBEB', color: '#92400E', dot: '#F59E0B' },
  approved: { label: 'Approved', bg: '#ECFDF5', color: '#065F46', dot: '#10B981' },
  canceled: { label: 'Canceled', bg: '#FEF2F2', color: '#991B1B', dot: '#EF4444' },
}

const StatusBadge = ({ status }) => {
  const key = String(status || 'pending').toLowerCase()
  const cfg = STATUS_CONFIG[key] || STATUS_CONFIG.pending
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '5px',
        padding: '3px 10px',
        borderRadius: '20px',
        fontSize: '11px',
        fontWeight: 700,
        letterSpacing: '0.3px',
        background: cfg.bg,
        color: cfg.color,
        fontFamily: 'Inter, system-ui, sans-serif',
        whiteSpace: 'nowrap',
      }}
    >
      <span
        style={{
          width: '6px',
          height: '6px',
          borderRadius: '50%',
          background: cfg.dot,
          flexShrink: 0,
        }}
      />
      {cfg.label}
    </span>
  )
}

const formatDisplayDate = (dateStr) => {
  if (!dateStr) return '—'
  const date = new Date(dateStr)
  if (Number.isNaN(date.getTime())) return dateStr
  return date.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

const extractLeaveList = (response) => {
  const body = response?.data
  if (Array.isArray(body?.data)) return body.data
  if (Array.isArray(body)) return body
  return []
}

const getApiErrorMessage = (error, fallback) => {
  const data = error?.response?.data
  if (data?.message) return data.message
  if (data?.errors && typeof data.errors === 'object') {
    const firstError = Object.values(data.errors).flat()[0]
    if (firstError) return firstError
  }
  return fallback
}

export default function TakeLeave() {
  const { logout } = useAuth()
  const { isCollapsed } = useSidebar()
  const { logo } = useInstitute()

  const [marginLeft, setMarginLeft] = useState('0px')
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false,
  )
  const [logoError, setLogoError] = useState(false)
  const [showForm, setShowForm] = useState(false)
  const [leaves, setLeaves] = useState([])
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [formData, setFormData] = useState({
    reason: '',
    from: '',
    to: '',
  })
  const [errors, setErrors] = useState({})
  const [editingLeave, setEditingLeave] = useState(null)
  const [editFormData, setEditFormData] = useState({
    reason: '',
    from: '',
    to: '',
  })
  const [editErrors, setEditErrors] = useState({})
  const [updatingLeave, setUpdatingLeave] = useState(false)

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 992) {
        const expandedWidth = 245
        const collapsedWidth = 85
        const gap = 1
        setMarginLeft(
          isCollapsed ? `${collapsedWidth + gap}px` : `${expandedWidth + gap}px`,
        )
      } else {
        setMarginLeft('0px')
      }
      setIsMobile(window.innerWidth <= 768)
    }
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isCollapsed])

  const fetchLeaves = useCallback(async () => {
    setLoading(true)
    try {
      const response = await ApiEndpoints.getMyTeacherLeaves()
      setLeaves(extractLeaveList(response))
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load leave requests.'))
      setLeaves([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchLeaves()
  }, [fetchLeaves])

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }))
    }
  }

  const validateForm = () => {
    const nextErrors = {}

    if (!formData.from) {
      nextErrors.from = 'From date is required'
    }

    if (!formData.to) {
      nextErrors.to = 'To date is required'
    }

    if (formData.from && formData.to && formData.from > formData.to) {
      nextErrors.to = 'To date must be on or after From date'
    }

    setErrors(nextErrors)
    return Object.keys(nextErrors).length === 0
  }

  const resetForm = () => {
    setFormData({ reason: '', from: '', to: '' })
    setErrors({})
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validateForm()) return

    setSubmitting(true)
    try {
      const payload = {
        from_date: formData.from,
        to_date: formData.to,
      }

      const trimmedReason = formData.reason.trim()
      if (trimmedReason) {
        payload.leave_description = trimmedReason
      }

      const response = await ApiEndpoints.applyTeacherLeave(payload)
      toast.success(
        response.data?.message || 'Leave request submitted successfully.',
      )
      resetForm()
      setShowForm(false)
      fetchLeaves()
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to submit leave request.'))
    } finally {
      setSubmitting(false)
    }
  }

  const getLeaveReason = (item) => item.leave_description || item.reason || '—'
  const getLeaveFrom = (item) => item.from_date
  const getLeaveTo = (item) => item.to_date
  const getLeaveStatus = (item) => String(item.status || 'pending').toLowerCase()
  const getAppliedOn = (item) => item.created_at

  const openEditModal = (leave) => {
    setEditingLeave(leave)
    setEditFormData({
      reason: leave.leave_description || leave.reason || '',
      from: leave.from_date || '',
      to: leave.to_date || '',
    })
    setEditErrors({})
  }

  const closeEditModal = () => {
    setEditingLeave(null)
    setEditFormData({ reason: '', from: '', to: '' })
    setEditErrors({})
  }

  const handleEditInputChange = (e) => {
    const { name, value } = e.target
    setEditFormData((prev) => ({ ...prev, [name]: value }))
    if (editErrors[name]) {
      setEditErrors((prev) => ({ ...prev, [name]: '' }))
    }
  }

  const validateEditForm = () => {
    const nextErrors = {}

    if (!editFormData.from) {
      nextErrors.from = 'From date is required'
    }

    if (!editFormData.to) {
      nextErrors.to = 'To date is required'
    }

    if (editFormData.from && editFormData.to && editFormData.from > editFormData.to) {
      nextErrors.to = 'To date must be on or after From date'
    }

    setEditErrors(nextErrors)
    return Object.keys(nextErrors).length === 0
  }

  const handleEditSubmit = async (e) => {
    e.preventDefault()
    if (!editingLeave?.id || !validateEditForm()) return

    setUpdatingLeave(true)
    try {
      const payload = {
        from_date: editFormData.from,
        to_date: editFormData.to,
      }

      const trimmedReason = editFormData.reason.trim()
      if (trimmedReason) {
        payload.leave_description = trimmedReason
      }

      const response = await ApiEndpoints.updateMyLeaveRequest(
        editingLeave.id,
        payload,
      )
      toast.success(
        response.data?.message || 'Leave request updated successfully.',
      )
      closeEditModal()
      fetchLeaves()
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to update leave request.'))
    } finally {
      setUpdatingLeave(false)
    }
  }

  return (
    <>
      <div
        style={{
          marginLeft,
          transition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
          position: 'relative',
          zIndex: 1,
          paddingLeft: isCollapsed ? '12px' : '12px',
          paddingRight: isCollapsed ? '12px' : '12px',
        }}
      >
        <div
          className="container-fluid p-0"
          style={{
            marginTop: window.innerWidth < 992 ? '55px' : '-12px',
          }}
        >
          <div style={{ marginLeft: isMobile ? '-13px' : '-40px' }}>
            <div
              className="d-flex justify-content-between align-items-center"
              style={{
                background: '#F3F4F6',
                padding: '12px 28px',
                borderBottom: '1px solid #D1D3D4',
              }}
            >
              <div className="d-flex align-items-center gap-3">
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    overflow: 'hidden',
                    background: '#223843',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {!logoError ? (
                    <img
                      src={logo}
                      alt="School Logo"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={() => setLogoError(true)}
                    />
                  ) : (
                    <LuGraduationCap size={20} style={{ color: '#fff' }} />
                  )}
                </div>
                <h3
                  className="m-0 fw-bold"
                  style={{
                    color: '#223843',
                    fontFamily: 'Inter, system-ui, sans-serif',
                  }}
                >
                  Take Leave
                </h3>
              </div>
              {!isMobile && <ProfileDropdown logout={logout} />}
            </div>
          </div>
        </div>

        <div
          className="container-fluid p-0 mt-4"
          style={{
            minHeight: '100vh',
            padding: isCollapsed ? '32px' : '40px',
          }}
        >
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
            <button
              type="button"
              onClick={() => setShowForm((prev) => !prev)}
              style={{
                borderRadius: '10px',
                padding: '8px 14px',
                height: '44px',
                border: 'none',
                backgroundColor: '#223843',
                color: '#ffffff',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                whiteSpace: 'nowrap',
                fontFamily: 'Inter, system-ui, sans-serif',
                fontSize: '13px',
                fontWeight: 650,
              }}
            >
              <BsPlusLg size={16} />
              Apply New Leave
            </button>

            <button
              type="button"
              onClick={fetchLeaves}
              disabled={loading}
              style={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                backgroundColor: '#10B981',
                border: 'none',
                color: '#fff',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.6 : 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              title="Refresh"
            >
              <BsArrowClockwise size={20} />
            </button>
          </div>

          {showForm && (
            <Card
              className="mb-4"
              style={{ border: '1px solid #D1D3D4', borderRadius: 12 }}
            >
              <Card.Body className="p-4">
                <h5
                  className="mb-4 fw-bold"
                  style={{
                    color: '#223843',
                    fontFamily: 'Inter, system-ui, sans-serif',
                  }}
                >
                  Apply New Leave
                </h5>
                <Form onSubmit={handleSubmit}>
                  <Row className="g-3 text-start">
                    <Col xs={12}>
                      <Form.Label className="fw-semibold text-muted" style={{ fontSize: 13 }}>
                        Reason
                      </Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        name="reason"
                        value={formData.reason}
                        onChange={handleInputChange}
                        placeholder="Enter reason for leave"
                        disabled={submitting}
                        isInvalid={!!errors.reason}
                        style={{ borderRadius: 8 }}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.reason}
                      </Form.Control.Feedback>
                    </Col>
                    <Col xs={12} md={6}>
                      <Form.Label className="fw-semibold text-muted" style={{ fontSize: 13 }}>
                        From
                      </Form.Label>
                      <Form.Control
                        type="date"
                        name="from"
                        value={formData.from}
                        onChange={handleInputChange}
                        disabled={submitting}
                        isInvalid={!!errors.from}
                        style={{ borderRadius: 8 }}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.from}
                      </Form.Control.Feedback>
                    </Col>
                    <Col xs={12} md={6}>
                      <Form.Label className="fw-semibold text-muted" style={{ fontSize: 13 }}>
                        To
                      </Form.Label>
                      <Form.Control
                        type="date"
                        name="to"
                        value={formData.to}
                        onChange={handleInputChange}
                        disabled={submitting}
                        isInvalid={!!errors.to}
                        style={{ borderRadius: 8 }}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.to}
                      </Form.Control.Feedback>
                    </Col>
                  </Row>

                  <div className="d-flex flex-wrap gap-2 mt-4">
                    <button
                      type="submit"
                      disabled={submitting}
                      style={{
                        borderRadius: '10px',
                        padding: '8px 18px',
                        border: 'none',
                        backgroundColor: '#223843',
                        color: '#ffffff',
                        cursor: submitting ? 'not-allowed' : 'pointer',
                        opacity: submitting ? 0.7 : 1,
                        fontFamily: 'Inter, system-ui, sans-serif',
                        fontSize: '13px',
                        fontWeight: 600,
                      }}
                    >
                      {submitting ? 'Submitting...' : 'Submit Leave'}
                    </button>
                    <button
                      type="button"
                      disabled={submitting}
                      onClick={() => {
                        resetForm()
                        setShowForm(false)
                      }}
                      style={{
                        borderRadius: '10px',
                        padding: '8px 18px',
                        border: '1px solid #D1D3D4',
                        backgroundColor: '#ffffff',
                        color: '#223843',
                        cursor: submitting ? 'not-allowed' : 'pointer',
                        fontFamily: 'Inter, system-ui, sans-serif',
                        fontSize: '13px',
                        fontWeight: 600,
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          )}

          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" style={{ color: '#223843' }} />
            </div>
          ) : (
            <Card style={{ border: '1px solid #D1D3D4', borderRadius: 12 }}>
              <Table className="table mb-0 text-start">
                <Thead>
                  <Tr style={{ backgroundColor: '#F3F4F6' }}>
                    <Th>#</Th>
                    <Th>Reason</Th>
                    <Th>From</Th>
                    <Th>To</Th>
                    <Th>Status</Th>
                    <Th>Applied On</Th>
                    <Th className="text-center">Action</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {leaves.length > 0 ? (
                    leaves.map((leave, index) => {
                      const status = getLeaveStatus(leave)
                      const isPending = status === 'pending'
                      const isUpdatingThis =
                        updatingLeave && editingLeave?.id === leave.id

                      return (
                      <Tr
                        key={leave.id || `${getLeaveFrom(leave)}-${index}`}
                        style={{
                          backgroundColor:
                            index % 2 === 0 ? '#ffffff' : 'rgba(243, 244, 246, 0.4)',
                        }}
                      >
                        <Td>{index + 1}</Td>
                        <Td
                          style={{
                            minWidth: isMobile ? 'auto' : '180px',
                            maxWidth: isMobile ? '100%' : '340px',
                            verticalAlign: 'top',
                            padding: isMobile ? '12px 10px' : '12px 16px',
                          }}
                        >
                          <ExpandableReasonText
                            text={getLeaveReason(leave)}
                            isMobile={isMobile}
                            modalTitle="Leave Details"
                            modalDetails={[
                              { label: 'S.No.', value: index + 1 },
                              { label: 'From', value: formatDisplayDate(getLeaveFrom(leave)) },
                              { label: 'To', value: formatDisplayDate(getLeaveTo(leave)) },
                              {
                                label: 'Status',
                                value:
                                  STATUS_CONFIG[String(getLeaveStatus(leave)).toLowerCase()]
                                    ?.label || getLeaveStatus(leave),
                              },
                              {
                                label: 'Applied On',
                                value: formatDisplayDate(getAppliedOn(leave)),
                              },
                            ]}
                          />
                        </Td>
                        <Td>{formatDisplayDate(getLeaveFrom(leave))}</Td>
                        <Td>{formatDisplayDate(getLeaveTo(leave))}</Td>
                        <Td>
                          <StatusBadge status={getLeaveStatus(leave)} />
                        </Td>
                        <Td>{formatDisplayDate(getAppliedOn(leave))}</Td>
                        <Td className="text-center">
                          {isPending ? (
                            <button
                              type="button"
                              onClick={() => openEditModal(leave)}
                              disabled={isUpdatingThis}
                              title="Edit leave"
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '6px',
                                borderRadius: '8px',
                                padding: isMobile ? '8px 12px' : '6px 12px',
                                minHeight: isMobile ? '36px' : '32px',
                                border: '1px solid #223843',
                                backgroundColor: '#ffffff',
                                color: '#223843',
                                fontSize: '12px',
                                fontWeight: 600,
                                cursor: isUpdatingThis ? 'not-allowed' : 'pointer',
                                opacity: isUpdatingThis ? 0.6 : 1,
                                fontFamily: 'Inter, system-ui, sans-serif',
                              }}
                            >
                              <BsPencilFill size={14} />
                              {!isMobile && 'Edit'}
                            </button>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </Td>
                      </Tr>
                      )
                    })
                  ) : (
                    <Tr>
                      <Td colSpan={7} className="text-center py-4 text-muted">
                        No leave requests found.
                      </Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
            </Card>
          )}
        </div>
      </div>

      <style>
        {`
          .edit-leave-modal .modal-dialog {
            max-width: min(920px, calc(100vw - 24px));
            width: 100%;
            margin: 12px auto;
          }
          @media (max-width: 768px) {
            .edit-leave-modal .modal-dialog {
              max-width: calc(100vw - 16px);
              margin: 8px auto;
            }
          }
        `}
      </style>

      <Modal
        show={Boolean(editingLeave)}
        onHide={closeEditModal}
        centered
        scrollable
        size="xl"
        dialogClassName="edit-leave-modal"
      >
        <Modal.Header
          closeButton={!updatingLeave}
          style={{
            padding: isMobile ? '16px 18px' : '20px 28px',
            borderBottom: '1px solid #E5E7EB',
          }}
        >
          <Modal.Title
            style={{
              fontFamily: 'Inter, system-ui, sans-serif',
              fontWeight: 700,
              fontSize: isMobile ? '20px' : '24px',
              color: '#223843',
            }}
          >
            Edit Leave Request
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleEditSubmit}>
          <Modal.Body
            style={{
              fontFamily: 'Inter, system-ui, sans-serif',
              padding: isMobile ? '20px 18px' : '28px 32px',
            }}
          >
            <Row className="g-4 text-start">
              <Col xs={12}>
                <Form.Label className="fw-semibold text-muted" style={{ fontSize: 14 }}>
                  Reason
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={isMobile ? 5 : 6}
                  name="reason"
                  value={editFormData.reason}
                  onChange={handleEditInputChange}
                  placeholder="Enter reason for leave"
                  disabled={updatingLeave}
                  style={{
                    borderRadius: 10,
                    fontSize: '15px',
                    padding: '12px 14px',
                    minHeight: isMobile ? '120px' : '160px',
                  }}
                />
              </Col>
              <Col xs={12} md={6}>
                <Form.Label className="fw-semibold text-muted" style={{ fontSize: 14 }}>
                  From
                </Form.Label>
                <Form.Control
                  type="date"
                  name="from"
                  value={editFormData.from}
                  onChange={handleEditInputChange}
                  disabled={updatingLeave}
                  isInvalid={!!editErrors.from}
                  style={{
                    borderRadius: 10,
                    fontSize: '15px',
                    padding: '12px 14px',
                    height: '48px',
                  }}
                />
                <Form.Control.Feedback type="invalid">
                  {editErrors.from}
                </Form.Control.Feedback>
              </Col>
              <Col xs={12} md={6}>
                <Form.Label className="fw-semibold text-muted" style={{ fontSize: 14 }}>
                  To
                </Form.Label>
                <Form.Control
                  type="date"
                  name="to"
                  value={editFormData.to}
                  onChange={handleEditInputChange}
                  disabled={updatingLeave}
                  isInvalid={!!editErrors.to}
                  style={{
                    borderRadius: 10,
                    fontSize: '15px',
                    padding: '12px 14px',
                    height: '48px',
                  }}
                />
                <Form.Control.Feedback type="invalid">
                  {editErrors.to}
                </Form.Control.Feedback>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer
            style={{
              padding: isMobile ? '16px 18px' : '20px 28px',
              borderTop: '1px solid #E5E7EB',
              gap: '10px',
            }}
          >
            <button
              type="button"
              onClick={closeEditModal}
              disabled={updatingLeave}
              style={{
                borderRadius: '10px',
                padding: isMobile ? '12px 20px' : '10px 22px',
                border: '1px solid #D1D3D4',
                backgroundColor: '#ffffff',
                color: '#223843',
                cursor: updatingLeave ? 'not-allowed' : 'pointer',
                fontFamily: 'Inter, system-ui, sans-serif',
                fontSize: '14px',
                fontWeight: 600,
                minWidth: isMobile ? '48%' : '120px',
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={updatingLeave}
              style={{
                borderRadius: '10px',
                padding: isMobile ? '12px 20px' : '10px 22px',
                border: 'none',
                backgroundColor: '#223843',
                color: '#ffffff',
                cursor: updatingLeave ? 'not-allowed' : 'pointer',
                opacity: updatingLeave ? 0.7 : 1,
                fontFamily: 'Inter, system-ui, sans-serif',
                fontSize: '14px',
                fontWeight: 600,
                minWidth: isMobile ? '48%' : '140px',
              }}
            >
              {updatingLeave ? 'Saving...' : 'Save Changes'}
            </button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  )
}
